<template>
  <div :class="invisible ? 'tw-invisible tw-h-0 tw-overflow-hidden' : ''">
    <canvas
      :id="id"
      :class="canvasClass"
    />
  </div>
</template>


<script>
import Chart from 'chart.js/auto';


export default {
  name: 'ChartWrapper',
  components: { /* Subcomponents */ },
  props: {
    name: {
      type: String,
      default: 'Chart',
    },
    canvasClass: {
      type: String,
      default: '',
    },
    chartConfig: {
      type: Object,
      required: true,
    },
    fontSize: {
      type: String,
      default: null,
    },
    exportImage: {
      type: Boolean,
      default: false,
    },
    invisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      id: null,
      chart: null,
      datasetLabels: [],
    };
  },
  computed: { /*
    Creates a new property
    Updates when any dependant property changes */
  },
  watch: { /*
    Watches an existing property
    Only runs when the watched property changes */
    chartConfig() {
      this.updateChart();
    },
  },
  created() {
    this.id = 'chart-' + this.createID();
  },
  mounted() {
    this.createChart();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
    this.$emit('imageRemoved', this.id);
  },
  methods: {
    createID() {
      return Math.random().toString(36).substring(2, 9);
    },
    getContext() {
      return document.getElementById(this.id);
    },
    createChart() {
      const { type, labels, datasets, options, formats } = this.chartConfig;

      const chartOptions = this.refineChartOptions(type, options, formats);

      this.chart = new Chart(this.getContext(), {
        type,
        data: {
          labels,
          datasets: this.clipDatasetLabels(datasets),
        },
        options: chartOptions,
      });
      this.getContext().onclick = this.clickHandler;

      this.scheduleImageUpdate();
    },
    clipDatasetLabels(datasets) {
      if (!datasets) {
        this.datasetLabels = [];
        return datasets;
      }

      this.datasetLabels = datasets.map(set => set.label);
      const newDatasets = JSON.parse(JSON.stringify(datasets));

      const maxLength = 50;
      newDatasets.forEach(set => {
        if (set?.label?.length > maxLength) {
          set.label = set.label.substring(0, maxLength) + '...';
        }
      });
      return newDatasets;
    },
    refineChartOptions(type, options, formats) {
      // Creates a deep copy so web and slide charts can have different options
      const localOptions = JSON.parse(JSON.stringify(options || {}));

      this.setLegendPosition(localOptions);
      this.overrideFontSize(localOptions, type);

      if (type !== 'pie') {
        this.setAxisFormat(localOptions, 'x', formats?.x);
        this.setAxisFormat(localOptions, 'y', formats?.y);
      }

      const getFormattedValue = raw => {
        const formatter = this.getFormatter(formats?.y);
        return formatter ? formatter(raw) : raw;
      };

      localOptions.plugins.tooltip = {
        callbacks: {
          title: function(context) {
            return context[0].dataset.label;
          },
          label: function(context) {
            return `${context.label}: ${getFormattedValue(context.raw)}`;
          },
        },
      };
      return { responsive: true, ...localOptions };
    },
    clickHandler(event) {
      const points = this.chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
      if (!points?.length) return;
      const point = points[0];

      const dataset = {
        ...this.chart.data.datasets[point.datasetIndex],
        label: this.datasetLabels[point.datasetIndex],
      };

      const clickData = {
        label: this.chart.data.labels[point.index],
        value: dataset,
        options: this.chartConfig.options,
      };

      this.$emit('chartClick', clickData);
    },
    setLegendPosition(options) {
      const position = options?.plugins?.legend?.position;
      this.overrideOption(options, 'plugins.legend.position', position || 'bottom');
    },
    overrideFontSize(options, chartType) {
      const size = parseInt(this.fontSize);
      if (!size) return;

      if (chartType !== 'pie') {
        this.overrideOption(options, 'scales.x.ticks.font.size', size);
        this.overrideOption(options, 'scales.y.ticks.font.size', size);
      }
      this.overrideOption(options, 'plugins.legend.labels.font.size', size);
      this.overrideOption(options, 'plugins.title.font.size', size);
    },
    setAxisFormat(options, axis, format) {
      const formatter = this.getFormatter(format);
      if (!formatter) return;

      this.overrideOption(options, `scales.${axis}.ticks.callback`, formatter);
    },
    getFormatter(format) {
      if (!format) return null;

      const formatters = {
        percent: this.toPercent,
        currency: this.toCurrency,
        labels: this.formatLabels,
      };
      return formatters[format];
    },
    // Allows us to override a specific nested option without clearing siblings
    // Takes advantage of pass by reference behavior
    overrideOption(obj, path, value) {
      const keys = path.split('.');
      let current = obj; // Start with the input object

      // Loop over each key except the last one
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        // If the key is missing initialize an empty object
        if (!current[key]) current[key] = {};

        // Move to the next level in the object
        current = current[key];
      }

      // Set the value on the last key
      current[keys[keys.length - 1]] = value;

      // Original object is modified in place so no need to return anything
    },
    toPercent(value, index, ticks) {
      return (value * 100).toFixed(1) + '%';
    },
    toCurrency(value, index, ticks) {
      return '$' + value.toLocaleString();
    },
    formatLabels(value) {
      const label = this.chartConfig.labels[value];
      const maxLength = 5;
      if (label.length <= maxLength) return label;
      return `${label.substring(0, maxLength)}...`;
    },
    updateChart() {
      const { type, labels, datasets, options, formats } = this.chartConfig;

      if (labels && datasets) {
        this.chart.data.labels = labels;
        this.chart.data.datasets = this.clipDatasetLabels(datasets);
        this.chart.options = this.refineChartOptions(type, options, formats);

        this.chart.update();
        this.scheduleImageUpdate();
      } else {
        const msg = 'Cannot update chart because data is missing';
        console.error(msg);
      }
    },
    scheduleImageUpdate() {
      if (this.exportImage) {
        this.updateImage();
      }
    },
    updateImage() {
      // const image = this.chart.toBase64Image('image/png', 1);
      this.$emit('imageChanged', { chart: this.chart, id: this.id });
    },
  },
};
</script>


<style scoped>

</style>
